import React, { useState } from "react";
import { StyledButton, StyledButtonStyle } from "ee-react-components";
import { CommandHistoryTable } from "../CommandHistoryTable";
import { ChargerControlCommandTab } from "../ChargerControlCommandTab";
import { CoolioCommandTab } from "../CoolioCommandTab";
/**
 * Available tabs in the charger command dialog.
 */
var CommandTab;
(function (CommandTab) {
    /**
     * SKSG V2 charger control commands tab (firmware update and reboot).
     */
    CommandTab["SKSG_V2"] = "SKSG_V2";
    /**
     * COOLIO commands tab (only for EEV3 chargers).
     */
    CommandTab["COOLIO"] = "COOLIO";
    /**
     * Command log/history tab.
     */
    CommandTab["COMMAND_LOG"] = "COMMAND_LOG";
})(CommandTab || (CommandTab = {}));
/**
 * Charger variants based on model naming conventions.
 */
var ChargerVariant;
(function (ChargerVariant) {
    /**
     * EEV3 charger model.
     */
    ChargerVariant["EEV3"] = "EEV3";
    /**
     * SKSG V2 chargers.
     */
    ChargerVariant["SKSG_V2"] = "SKSG_V2";
    /**
     * Unknown/unclassified charger model.
     */
    ChargerVariant["UNKNOWN"] = "UNKNOWN";
})(ChargerVariant || (ChargerVariant = {}));
/**
 * Determines the charger variant based on the model name.
 * @param model - The model name of the charger (e.g., 'Eev3_1234', 'FC200K', 'DP400K')
 * @returns The charger variant
 */
function getChargerVariant(model) {
    if (model.toLowerCase().startsWith("eev3")) {
        return ChargerVariant.EEV3;
    }
    if (model.toLowerCase().startsWith("fc200k") ||
        model.toLowerCase().startsWith("dp400k") ||
        model.toLowerCase().startsWith("cs32") ||
        model.toLowerCase().startsWith("cs36") ||
        model.toLowerCase().startsWith("cs40")) {
        return ChargerVariant.SKSG_V2;
    }
    return ChargerVariant.UNKNOWN;
}
/**
 * Dialog component for managing charger commands and viewing command history.
 * Displays charger information and provides tabs for general commands, COOLIO commands
 * (for RetailEdge chargers), and command history.
 */
export var ChargerCommandDialog = function (_a) {
    var chargerId = _a.chargerId, friendlyId = _a.friendlyId, ocppId = _a.ocppId, model = _a.model, onClose = _a.onClose;
    var chargerVariant = getChargerVariant(model);
    var isEev3 = chargerVariant === ChargerVariant.EEV3;
    var isSksgV2 = chargerVariant === ChargerVariant.SKSG_V2;
    var _b = useState(CommandTab.COMMAND_LOG), activeTab = _b[0], setActiveTab = _b[1];
    return (React.createElement("div", { className: "bg-black-1150 rounded-[20px] p-[32px] flex flex-col gap-y-[32px] min-w-[650px] max-w-[90vw] max-h-[90vh]" },
        React.createElement("div", { className: "flex flex-col gap-y-1 flex-shrink-0" },
            React.createElement("label", { className: "font-N6 text-white w-full text-left" },
                "Command Charger ",
                friendlyId,
                " (",
                ocppId,
                ")"),
            React.createElement("label", { className: "font-N7 text-gray-400 w-full text-left" }, model)),
        React.createElement("div", { className: "flex flex-row gap-x-2 border-b border-gray-700 flex-shrink-0" },
            isSksgV2 && (React.createElement("button", { className: "font-N7 px-4 py-2 ".concat(activeTab === CommandTab.SKSG_V2
                    ? "text-white border-b-2 border-white"
                    : "text-gray-400"), onClick: function () { return setActiveTab(CommandTab.SKSG_V2); } }, "SKSG V2")),
            isEev3 && (React.createElement("button", { className: "font-N7 px-4 py-2 ".concat(activeTab === CommandTab.COOLIO
                    ? "text-white border-b-2 border-white"
                    : "text-gray-400"), onClick: function () { return setActiveTab(CommandTab.COOLIO); } }, "COOLIO")),
            React.createElement("button", { className: "font-N7 px-4 py-2 ".concat(activeTab === CommandTab.COMMAND_LOG
                    ? "text-white border-b-2 border-white"
                    : "text-gray-400"), onClick: function () { return setActiveTab(CommandTab.COMMAND_LOG); } }, "Log")),
        React.createElement("div", { className: "flex flex-col gap-y-4 overflow-auto flex-1 min-h-0" },
            activeTab === CommandTab.SKSG_V2 && isSksgV2 && (React.createElement(ChargerControlCommandTab, { chargerId: chargerId, model: model })),
            activeTab === CommandTab.COOLIO && isEev3 && (React.createElement(CoolioCommandTab, { chargerId: chargerId })),
            activeTab === CommandTab.COMMAND_LOG && (React.createElement("div", { className: "text-white font-N7" },
                React.createElement(CommandHistoryTable, { chargerId: chargerId })))),
        React.createElement("div", { className: "flex flex-row gap-x-4 flex-shrink-0" },
            React.createElement(StyledButton, { text: "CLOSE", style: StyledButtonStyle.Gray, onClick: onClose }))));
};
